import { X, Loader2, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-0.5 text-xs">
    <span className="text-zinc-500 shrink-0">{k}</span>
    <span className="text-zinc-200 text-right break-all">{String(v ?? "—")}</span>
  </div>
);

const Section = ({ title, children }) => (
  <div className="border border-white/10 p-3">
    <div className="text-[10px] uppercase tracking-[0.25em] text-rose-500 mb-2">{title}</div>
    {children}
  </div>
);

export default function LiveDiagnosticsModal({ open, onClose, loading, data, error }) {
  const [sending, setSending] = useState(false);
  const sendToNeo = async () => {
    setSending(true);
    try {
      const s = data?.stream || {};
      await api.post("/admin/neo/tickets", {
        title: `Live diagnostics — ${s.title || s.id}`,
        description: `Auto-created from Admin → Live → Diagnose.\nHost: @${s.host_handle || s.host_id}\nLive: ${s.is_live}\nRoom active: ${data?.livekit?.room_active}\nFull diagnostics snapshot attached.`,
        priority: "medium",
        area: "live",
        environment: window.location.hostname.includes("preview") ? "preview" : "production",
        request_type: "diagnostic",
        diagnostics_snapshot: data,
      });
      toast.success("Ticket created in Neo Inbox with diagnostics attached.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create ticket.");
    } finally {
      setSending(false);
    }
  };
  if (!open) return null;
  const s = data?.stream || {};
  const lk = data?.livekit || {};
  const app = data?.app || {};
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-start justify-center overflow-y-auto p-4" data-testid="live-diagnostics-modal">
      <div className="bg-[#0d0d0d] border border-white/10 w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="text-sm font-medium">Live Diagnostics {s.title ? `— ${s.title}` : ""}</div>
          <div className="flex items-center gap-3">
            {data && (
              <button onClick={sendToNeo} disabled={sending} data-testid="live-diag-send-to-neo"
                className="text-xs text-cyan-300 border border-cyan-700/60 hover:border-cyan-400 px-2.5 py-1.5 flex items-center gap-1.5 disabled:opacity-50">
                <Send size={12} /> {sending ? "Sending…" : "Send to Neo"}
              </button>
            )}
            <button onClick={onClose} data-testid="live-diag-close" className="text-zinc-400 hover:text-white"><X size={16} /></button>
          </div>
        </div>
        <div className="p-4 space-y-3">
          {loading && <div className="flex items-center gap-2 text-sm text-zinc-400 py-8 justify-center"><Loader2 size={16} className="animate-spin" /> Fetching session state…</div>}
          {error && <div className="text-sm text-rose-400 py-4">{error}</div>}
          {!loading && data && (
            <>
              <Section title="Stream">
                <Row k="Host" v={s.host_handle ? `@${s.host_handle}` : s.host_id} />
                <Row k="Live" v={s.is_live} />
                <Row k="Locked / Private" v={s.is_locked} />
                <Row k="Community (Vault)" v={s.is_community} />
                <Row k="iOS-safe" v={s.ios_safe} />
                <Row k="Viewer count" v={s.viewer_count} />
                <Row k="Started" v={s.started_at} />
                {s.ended_at && <Row k="Ended" v={s.ended_at} />}
                {s.recording && <Row k="Recording" v={s.recording.status || s.recording.egress_id} />}
                {s.stage && <Row k="Stage layout" v={`${s.stage.layout}${s.stage.spotlight_id ? " · spotlight" : ""}${s.stage.pinned_id ? " · pinned" : ""}`} />}
              </Section>
              <Section title="LiveKit Room">
                <Row k="Configured" v={lk.configured} />
                <Row k="Room" v={lk.room_name} />
                <Row k="Room active" v={lk.room_active} />
                {lk.room && (
                  <>
                    <Row k="Participants" v={lk.room.num_participants} />
                    <Row k="Publishers" v={lk.room.num_publishers} />
                    <Row k="Recording active" v={lk.room.active_recording} />
                  </>
                )}
                {lk.error && <Row k="Error" v={lk.error} />}
              </Section>
              {(lk.participants || []).map((p) => (
                <Section key={p.identity} title={`Participant — ${p.handle ? `${p.display_name}` : p.name || p.identity}`}>
                  <Row k="State" v={p.state} />
                  <Row k="Publisher" v={p.is_publisher} />
                  <Row k="Hidden" v={p.permission?.hidden} />
                  <Row k="Can publish / subscribe" v={`${p.permission?.can_publish} / ${p.permission?.can_subscribe}`} />
                  {(p.tracks || []).length === 0 && <Row k="Tracks" v="none published" />}
                  {(p.tracks || []).map((t) => (
                    <Row key={t.sid} k={`${t.type} (${t.source})`} v={`${t.muted ? "MUTED" : "live"}${t.width ? ` · ${t.width}x${t.height}` : ""}${t.simulcast ? " · simulcast" : ""}`} />
                  ))}
                </Section>
              ))}
              <Section title="App Signals">
                <Row k="Chat WS subscribers (this pod)" v={app.ws_chat_subscribers_local} />
                <Row k="Presence rows" v={app.presence_count} />
                <Row k="Chat messages total" v={app.chat_messages_total} />
                <Row k="Gifts total" v={app.gifts_total} />
                {(app.recent_chat || []).map((m, i) => (
                  <Row key={`c${i}`} k={`chat ${m.display_name}`} v={`${(m.text || "").slice(0, 60)} · ${m.created_at}`} />
                ))}
                {(app.recent_gifts || []).map((g, i) => (
                  <Row key={`g${i}`} k={`gift ${g.sender_display_name || g.sender_handle || "member"}`} v={`${g.gift_key} · ${g.coins} coins · ${g.created_at}`} />
                ))}
                {(app.cohost_invites || []).map((c, i) => (
                  <Row key={`h${i}`} k="cohost invite" v={`${c.invitee_id?.slice(0, 8)} · ${c.status}`} />
                ))}
                {(app.battles || []).map((b) => (
                  <Row key={b.id} k={`battle ${b.status}`} v={`${b.host_score} : ${b.opponent_score} · ${b.duration_seconds}s`} />
                ))}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
